/**
 * Tipos para el sistema de plantillas de correo dinámicas
 * Similar a HubSpot: texto fijo + campos dinámicos reemplazables por módulo
 */

import type { EmailAddress } from './email'

export type TemplateModuleSource = 'shipments' | 'purchaseOrders'
export type TemplateVisibility = 'public' | 'private'

export interface TemplateDynamicField {
  /** Módulo de origen del campo */
  module: TemplateModuleSource
  /** ID/nombre del campo en el sistema (ej: 'milestones.eta', 'origin') */
  fieldId: string
  /** Label legible del campo (ej: 'ETA', 'Origen') */
  fieldLabel: string
  /** Sección del campo en el módulo (ej: 'estimatedTimes', 'references') */
  section?: string
  /** Tipo de dato del campo (ej: 'date', 'string', 'number') */
  fieldType?: string
  /** Formato de fecha si aplica */
  dateFormat?: string
  /** Valor por defecto si el campo no tiene valor */
  fallbackValue?: string
}

/** Destinatarios que se precargan en el redactor al aplicar la plantilla */
export interface TemplateDefaultRecipients {
  to: EmailAddress[]
  cc: EmailAddress[]
  bcc: EmailAddress[]
}

export interface EmailTemplate {
  id: string
  /** Nombre de la plantilla para identificarla */
  name: string
  /** Descripción opcional */
  description?: string
  /** Asunto del correo (puede contener campos dinámicos) */
  subject: string
  /** Cuerpo HTML del correo (puede contener campos dinámicos como chips) */
  bodyHtml: string
  /** Módulos de los que depende esta plantilla */
  modules: TemplateModuleSource[]
  /** Campos dinámicos utilizados en la plantilla (para referencia rápida) */
  dynamicFields: TemplateDynamicField[]
  /** Categoría o etiqueta para organizar */
  category?: string
  /** Visibilidad: 'public' (toda la compañía) o 'private' (solo el creador) */
  visibility: TemplateVisibility
  /** Si la plantilla está activa */
  isActive: boolean
  /** Campo de fecha que se auto-llenará al enviar la plantilla por primera vez */
  autoFillDateField?: {
    /** Módulo del campo (shipments, purchaseOrders, etc.) */
    module: TemplateModuleSource
    /** ID del campo (ej: 'milestones.firstNotificationDate', 'notificationDate') */
    fieldId: string
    /** Label del campo para mostrar en UI */
    fieldLabel: string
  }
  /** Ids de tipo de documento a adjuntar automáticamente al aplicar la plantilla */
  autoAttachDocumentTypes?: string[]
  /** Destinatarios que se precargan al aplicar la plantilla */
  defaultRecipients?: TemplateDefaultRecipients
  /** Quién la creó */
  createdBy: string
  createdAt: string
  updatedBy?: string
  updatedAt?: string
  /** Cantidad de veces usada */
  usageCount: number
}

export interface EmailTemplateCreatePayload {
  name: string
  description?: string
  subject: string
  bodyHtml: string
  modules: TemplateModuleSource[]
  dynamicFields: TemplateDynamicField[]
  category?: string
  visibility?: TemplateVisibility
  autoFillDateField?: {
    module: TemplateModuleSource
    fieldId: string
    fieldLabel: string
  }
  autoAttachDocumentTypes?: string[]
  defaultRecipients?: TemplateDefaultRecipients
}

export interface EmailTemplateUpdatePayload {
  name?: string
  description?: string
  subject?: string
  bodyHtml?: string
  modules?: TemplateModuleSource[]
  dynamicFields?: TemplateDynamicField[]
  category?: string
  isActive?: boolean
  visibility?: TemplateVisibility
  autoFillDateField?: {
    module: TemplateModuleSource
    fieldId: string
    fieldLabel: string
  } | null
  autoAttachDocumentTypes?: string[]
  defaultRecipients?: TemplateDefaultRecipients
}

/**
 * Representación de un campo dinámico dentro del HTML del editor.
 * Se renderiza como un chip/badge no editable con data attributes.
 * Formato en el HTML:
 * <span class="dynamic-field" 
 *   data-module="shipments" 
 *   data-field-id="milestones.eta" 
 *   data-field-label="ETA"
 *   contenteditable="false">
 *   {{shipments.milestones.eta}}
 * </span>
 */
export const DYNAMIC_FIELD_CLASS = 'dynamic-field'
export const DYNAMIC_TABLE_CLASS = 'dynamic-table'
export const DYNAMIC_FIELD_REGEX = /\{\{(\w+)\.([^}]+)\}\}/g

/**
 * Columna de una tabla dinámica dentro de una plantilla.
 * Cada columna mapea un campo del módulo a una columna de la tabla HTML.
 */
export interface TemplateDynamicTableColumn {
  fieldId: string
  fieldLabel: string
  fieldType?: string
  dateFormat?: string
}

/**
 * Resultado de resolver una plantilla con datos reales
 */
export interface ResolvedTemplate {
  subject: string
  bodyHtml: string
  /** Campos que no pudieron ser resueltos (campo no encontrado en los datos) */
  unresolvedFields: TemplateDynamicField[]
}

/**
 * Datos fuente para resolver una plantilla
 */
export interface TemplateDataContext {
  shipments?: Record<string, any>
  purchaseOrders?: Record<string, any>
  /** Arrays de registros individuales para resolver tablas dinámicas */
  purchaseOrders_items?: Record<string, any>[]
  shipments_items?: Record<string, any>[]
  /** Sub-colecciones del embarque */
  shipments_containers?: Record<string, any>[]
  /** Tablas de cotización del embarque */
  shipments_quotationContainers?: Record<string, any>[]
  shipments_quotationCharges?: Record<string, any>[]
  /** Acceso genérico por data-source */
  [key: string]: Record<string, any> | Record<string, any>[] | undefined
}
